import { getContracts, BSC_TESTNET_CHAIN_ID, ARC_TESTNET_CHAIN_ID, type ChainContracts } from './contracts'
import { bscTestnet } from './chains'

export interface Token {
  address: string
  symbol: string
  name: string
  decimals: number
  logoURI?: string
}

export const NATIVE_TOKEN_ADDRESS = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'

// ========== BSC Testnet tokens ==========
export const NATIVE_TOKEN: Token = {
  address: NATIVE_TOKEN_ADDRESS,
  symbol: 'tBNB',
  name: 'Testnet BNB',
  decimals: 18,
}

const BSC = getContracts(BSC_TESTNET_CHAIN_ID)

export const WBNB: Token = {
  address: BSC.WBNB,
  symbol: 'WBNB',
  name: 'Wrapped BNB',
  decimals: 18,
}

export const AXON: Token = {
  address: BSC.AxonToken,
  symbol: 'AXON',
  name: 'AxonSwap Token',
  decimals: 18,
}

export const mUSDC: Token = {
  address: BSC.mUSDC,
  symbol: 'mUSDC',
  name: 'Mock USDC',
  decimals: 6,
}

export const mUSDT: Token = {
  address: BSC.mUSDT,
  symbol: 'mUSDT',
  name: 'Mock USDT',
  decimals: 6,
}

export const mWBTC: Token = {
  address: BSC.mWBTC,
  symbol: 'mWBTC',
  name: 'Mock WBTC',
  decimals: 8,
}

export const mWETH: Token = {
  address: BSC.mWETH,
  symbol: 'mWETH',
  name: 'Mock WETH',
  decimals: 18,
}

export const DEFAULT_TOKENS: Token[] = [NATIVE_TOKEN, WBNB, AXON, mUSDC, mUSDT, mWBTC, mWETH]

export const CHAIN_CONFIG = bscTestnet

// ========== Multi-chain token helpers ==========

function buildTokensForChain(c: ChainContracts, nativeSym: string, nativeName: string, wrappedSym: string, wrappedName: string): {
  nativeToken: Token
  wrappedNativeToken: Token
  defaultTokens: Token[]
} {
  const nativeToken: Token = { address: NATIVE_TOKEN_ADDRESS, symbol: nativeSym, name: nativeName, decimals: 18 }
  const wrappedNativeToken: Token = { address: c.WBNB, symbol: wrappedSym, name: wrappedName, decimals: 18 }
  const axon: Token = { address: c.AxonToken, symbol: 'AXON', name: 'AxonSwap Token', decimals: 18 }
  const usdc: Token = { address: c.mUSDC, symbol: 'mUSDC', name: 'Mock USDC', decimals: 6 }
  const usdt: Token = { address: c.mUSDT, symbol: 'mUSDT', name: 'Mock USDT', decimals: 6 }
  const wbtc: Token = { address: c.mWBTC, symbol: 'mWBTC', name: 'Mock WBTC', decimals: 8 }
  const weth: Token = { address: c.mWETH, symbol: 'mWETH', name: 'Mock WETH', decimals: 18 }
  return {
    nativeToken,
    wrappedNativeToken,
    defaultTokens: [nativeToken, wrappedNativeToken, axon, usdc, usdt, wbtc, weth],
  }
}

const CHAIN_TOKENS: Record<number, ReturnType<typeof buildTokensForChain>> = {
  [BSC_TESTNET_CHAIN_ID]: buildTokensForChain(BSC, 'tBNB', 'Testnet BNB', 'WBNB', 'Wrapped BNB'),
  [ARC_TESTNET_CHAIN_ID]: buildTokensForChain(
    getContracts(ARC_TESTNET_CHAIN_ID), 'USDC', 'USDC', 'WUSDC', 'Wrapped USDC'
  ),
}

const DEFAULT_CHAIN_TOKENS = CHAIN_TOKENS[BSC_TESTNET_CHAIN_ID]

export function getNativeToken(chainId: number): Token {
  return (CHAIN_TOKENS[chainId] || DEFAULT_CHAIN_TOKENS).nativeToken
}

export function getWrappedNativeToken(chainId: number): Token {
  return (CHAIN_TOKENS[chainId] || DEFAULT_CHAIN_TOKENS).wrappedNativeToken
}

export function getDefaultTokens(chainId: number): Token[] {
  return (CHAIN_TOKENS[chainId] || DEFAULT_CHAIN_TOKENS).defaultTokens
}
